import ProfileAvatar from "./ProfileAvatar";
import {
  Button,
  IconButton,
  Link,
  MenuItem,
  Stack,
  styled,
  SvgIcon,
  Typography,
  Drawer,
  Divider,
  List,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Icon } from "@trimble/modus-cmx";

import { authSignOutRedirect } from "@/provider/AuthProvider/signOut";
import CopyableEmail from "@/components/ui/CopyableEmail";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { Fragment } from "react/jsx-runtime";

const DrawerLink = styled(Link)({
  textDecoration: "none",
  fontSize: "0.75rem",
  color: "inherit",
});

type ListItemProps = {
  label: string;
  href: string;
  icon?: React.ElementType;
};

const ListItem = (props: ListItemProps) => {
  const { label, href, icon } = props;
  return (
    <MenuItem component={Link} href={href} sx={{ px: 3, py: 1 }}>
      <ListItemText>{label}</ListItemText>
      <ListItemIcon sx={{ color: "inherit" }}>
        {icon && <SvgIcon component={icon} inheritViewBox />}
      </ListItemIcon>
    </MenuItem>
  );
};

type ProfileDrawerProps = {
  open?: boolean;
  onClose?: () => void;
  links?: {
    top?: ListItemProps[];
    middle?: ListItemProps[];
    bottom?: ListItemProps[];
  };
};

export default function ProfileDrawer(props: ProfileDrawerProps) {
  const { open, onClose, links } = props;

  const auth = useAuth();
  const { t } = useTranslation();

  const { given_name, family_name, email } = auth.user?.profile ?? {};

  const handleSignoutClick = () => {
    void authSignOutRedirect(auth.signoutRedirect);
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            bgcolor: "primary.main",
            color: "primary.contrastText",
            width: 400,
            maxWidth: "100vw",
          },
        },
      }}
    >
      <Stack sx={{ bgcolor: "info.main" }} py={3}>
        <Stack px={3} pb={3} gap={3}>
          <ProfileAvatar sx={{ width: 56, height: 56 }} />
          <IconButton
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "inherit",
            }}
            onClick={handleClose}
            aria-label={t("aria.close")}
          >
            <Icon name="close" />
          </IconButton>
          <Stack gap={0.5}>
            <Typography fontWeight={700} fontSize="1.125rem">
              {t("full_name", {
                firstName: given_name,
                lastName: family_name,
              })}
            </Typography>
            {email && (
              <CopyableEmail
                email={email}
                slotProps={{
                  typography: {
                    size: "sm",
                    style: { color: "inherit" },
                  },
                }}
              />
            )}
          </Stack>
        </Stack>
        <Divider sx={{ borderColor: "primary.main" }} />
        <List sx={{ pt: 2 }}>
          {links?.top?.map((link) => (
            <ListItem key={link.label} {...link} />
          ))}
        </List>
        <Button
          variant="outlined"
          color="inherit"
          sx={{ borderRadius: 6, mx: 3, my: 1 }}
          onClick={handleSignoutClick}
        >
          {t("profile_drawer.sign_out")}
        </Button>
      </Stack>
      <List sx={{ pt: 2 }}>
        <Typography
          px={3}
          py={1}
          textTransform="uppercase"
          fontWeight={700}
          fontSize="0.88rem"
        >
          {t("profile_drawer.explore_trimble")}
        </Typography>
        {links?.middle?.map((link) => (
          <ListItem key={link.label} {...link} />
        ))}
      </List>
      <Stack direction="row" gap={1.5} mx={3} my={1}>
        {links?.bottom?.map((link, i, l) => (
          <Fragment key={link.label}>
            <DrawerLink href={link.href}>{link.label}</DrawerLink>
            {i < l.length - 1 && <Divider orientation="vertical" />}
          </Fragment>
        ))}
      </Stack>
    </Drawer>
  );
}
